import {
  useEffect,
  useId,
  useRef,
  type MouseEvent,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'
import { useFocusTrap } from '../hooks/useFocusTrap'
import { useReducedMotion } from '../hooks/useReducedMotion'

export type ModalSize = 'sm' | 'md' | 'lg' | 'fullscreen'

export interface ModalProps {
  /** Whether the modal is currently visible. */
  open: boolean
  /** Called when the user dismisses the modal via Escape, backdrop, or close button. */
  onClose: () => void
  /** Optional header title. When set, the modal gets a titled header with a close button. */
  title?: ReactNode
  /** Content rendered inside the modal body. */
  children: ReactNode
  /** Size preset controlling max-width. Default 'md'. */
  size?: ModalSize
  /** Allow dismiss via backdrop click. Default true. */
  closeOnBackdrop?: boolean
  /** Optional additional className on the content container. */
  className?: string
  /** Accessible label when `title` isn't a plain string. */
  ariaLabel?: string
}

const SIZE_CLASSES: Record<ModalSize, string> = {
  sm: 'max-w-sm',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  fullscreen: 'max-w-none w-full h-full rounded-none',
}

/**
 * Generic modal shell: portaled dialog with backdrop, focus trap, body
 * scroll lock, Escape-to-close, and optional click-outside dismissal.
 *
 * Used internally by `ConfirmDialog` and exported for features that
 * need a custom-content modal.
 */
export function Modal({
  open,
  onClose,
  title,
  children,
  size = 'md',
  closeOnBackdrop = true,
  className = '',
  ariaLabel,
}: ModalProps) {
  const contentRef = useRef<HTMLDivElement>(null)
  const titleId = useId()
  const reduceMotion = useReducedMotion()

  useFocusTrap(contentRef, open)

  // Escape to close + body scroll lock.
  useEffect(() => {
    if (!open) return
    if (typeof document === 'undefined') return

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation()
        onClose()
      }
    }

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.body.style.overflow = previousOverflow
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [open, onClose])

  if (!open) return null
  if (typeof document === 'undefined') return null

  const handleBackdropClick = (event: MouseEvent<HTMLDivElement>) => {
    if (!closeOnBackdrop) return
    if (event.target === event.currentTarget) {
      onClose()
    }
  }

  const transition = reduceMotion
    ? ''
    : 'transition-all duration-200 ease-out animate-scale-in'

  const titleString = typeof title === 'string' ? title : undefined

  return createPortal(
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center p-4"
      role="presentation"
    >
      <div
        className={`absolute inset-0 bg-black/60 backdrop-blur-sm ${
          reduceMotion ? '' : 'animate-fade-in'
        }`}
        onClick={handleBackdropClick}
        aria-hidden="true"
      />

      <div
        ref={contentRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        aria-label={!title ? ariaLabel : undefined}
        tabIndex={-1}
        className={`relative w-full ${SIZE_CLASSES[size]} max-h-[90vh] flex flex-col bg-bg-secondary border border-border-primary rounded-lg shadow-cinematic-lg focus:outline-none ${transition} ${className}`}
      >
        {title && (
          <div className="flex items-center justify-between gap-4 p-5 border-b border-border-primary flex-shrink-0">
            <h2
              id={titleId}
              className="text-lg font-semibold text-text-primary"
            >
              {title}
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="p-2 rounded-md text-text-secondary hover:text-text-primary hover:bg-bg-tertiary transition-colors"
              aria-label={titleString ? `Close ${titleString}` : 'Close dialog'}
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        )}

        <div className="p-5 overflow-y-auto flex-1 text-text-primary">
          {children}
        </div>
      </div>
    </div>,
    document.body
  )
}

export default Modal
